/**
 * Phase 2C.4 — Publish Workflow components.
 *
 * Composition:
 *   <PublishSplitButton/>       — primary "Publish" + dropdown for targets + Save-Draft
 *   <PublishDialog/>            — checklist (6 items) + target picker + release notes
 *                                  → on confirm calls /publish; opens PublishSuccessDialog
 *   <PublishSuccessDialog/>     — confirmation summary + View Assessment CTA
 *   <PublishedBadge/>           — permanent badge showing current published version + target
 *   <FindReplaceDialog/>        — find/replace with affected-items preview
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Loader2, CheckCircle2, XCircle, Send, AlertTriangle,
  Sparkles, Search, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { getUiLocale } from '@/lib/dates';

const PUBLISH_TARGETS = [
  { code: 'talentcheck', label: 'TalentCheck' },
  { code: 'talentcompetency', label: 'TalentCompetency' },
  { code: 'talenttrain', label: 'TalentTrain' },
  { code: 'climara', label: 'CLIMARA' },
  { code: 'talentmap', label: 'TalentMap' },
  { code: 'internal_library', label: 'Internal Library' },
];

/* ============================================================================
 *                          PUBLISH SPLIT BUTTON
 * ========================================================================== */
export function PublishSplitButton({ questionnaire, onOpenPublish }) {
  const { t } = useTranslation();
  // UAT 07/17 lifecycle sync — publish is only legal from `approved`.
  const canPublish = questionnaire?.status === 'approved';
  return (
    <Button
      variant="default"
      size="sm"
      onClick={onOpenPublish}
      disabled={!canPublish}
      data-testid="studio-publish-button"
      className="h-7 text-xs gap-1"
    >
      <Send className="h-3 w-3" />
      {t('studio.publish.publish', { defaultValue: 'Publish' })}
    </Button>
  );
}

/* ============================================================================
 *                          PUBLISH DIALOG (checklist + form)
 * ========================================================================== */
export function PublishDialog({ open, onClose, assetId, questionnaire, adminApi, onPublished }) {
  const { t, i18n } = useTranslation();
  const isEs = (i18n.language || '').startsWith('es');
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [target, setTarget] = useState(questionnaire?.publish_target || '');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [confirmAcked, setConfirmAcked] = useState(false);

  const fetchReadiness = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.studioPublishReadiness(assetId);
      setReadiness(r);
    } catch (err) {
      toast.error(t('studio.publish.readinessError', { defaultValue: 'Could not load readiness.' }));
    } finally {
      setLoading(false);
    }
  }, [adminApi, assetId, t]);
  useEffect(() => { if (open) fetchReadiness(); }, [open, assetId, fetchReadiness]);

  const handlePublish = async (force = false) => {
    if (!target) {
      toast.error(t('studio.publish.targetRequired', { defaultValue: 'Pick a publish target.' }));
      return;
    }
    setPublishing(true);
    try {
      const out = await adminApi.studioPublish(assetId, {
        publish_target: target,
        release_notes: releaseNotes,
        force,
      });
      toast.success(t('studio.publish.success', { defaultValue: 'Published.' }));
      onPublished(out);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === 'confirmation_required' && !force) {
        // Confirm via second click after user acks.
        toast.info(t('studio.publish.warningConfirm', {
          defaultValue: '{{n}} warnings — review and confirm to continue.',
          n: detail.warning_count,
        }));
      } else if (err?.response?.status === 422) {
        toast.error(t('studio.publish.blocked', { defaultValue: 'Checklist blocks publishing — see below.' }));
        await fetchReadiness();
      } else {
        toast.error(detail?.message || detail || 'Publish failed');
      }
    } finally {
      setPublishing(false);
    }
  };

  const checklist = readiness?.checklist?.items || [];
  const blocksPublish = readiness?.checklist?.blocks_publish ?? true;
  const needsConfirmation = readiness?.publish_confirmation_required && !confirmAcked;
  const targetLabel = PUBLISH_TARGETS.find((t2) => t2.code === target)?.label || target;
  const sectionsCount = (questionnaire?._sectionsCount ?? 0);  // optional optimistic
  const itemsCount = (questionnaire?._itemsCount ?? 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="studio-publish-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" />
            {t('studio.publish.dialogTitle', { defaultValue: 'Publish assessment' })}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('studio.publish.dialogDesc', {
              defaultValue: 'Review the readiness checklist, choose a target, and add optional release notes.',
            })}
          </DialogDescription>
        </DialogHeader>

        {loading && !readiness && (
          <div className="py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
          </div>
        )}

        {readiness && (
          <div className="space-y-4 py-2">
            <ul className="space-y-1.5" data-testid="studio-publish-checklist">
              {checklist.map((item) => (
                <ChecklistRow key={item.code} item={item} isEs={isEs} />
              ))}
            </ul>

            <div>
              <label className="text-xs font-medium block mb-1">
                {t('studio.publish.target', { defaultValue: 'Publish target' })}
              </label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                data-testid="studio-publish-target-select"
                className="w-full h-9 text-sm border border-border rounded-md bg-background px-2"
              >
                <option value="">{t('studio.publish.selectTarget', { defaultValue: '— Select target —' })}</option>
                {PUBLISH_TARGETS.map((tg) => (
                  <option key={tg.code} value={tg.code} data-testid={`studio-publish-target-${tg.code}`}>
                    {tg.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium block mb-1">
                {t('studio.publish.releaseNotes', { defaultValue: 'Release notes (optional)' })}
              </label>
              <Textarea
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                placeholder={t('studio.publish.releaseNotesPlaceholder', {
                  defaultValue: 'What changed in this version? (Stored in version history.)',
                })}
                rows={3}
                data-testid="studio-publish-notes"
              />
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="studio-publish-confirmation-summary">
              <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                {t('studio.publish.aboutToPublish', { defaultValue: 'About to publish' })}
              </p>
              <p className="font-medium text-sm">{questionnaire.name}</p>
              <p className="text-xs text-muted-foreground mt-1">
                → <span className="font-semibold">{targetLabel || '—'}</span>
                {sectionsCount > 0 && ` · ${sectionsCount} ${t('studio.preview.sections', { defaultValue: 'sections' })}`}
                {itemsCount > 0 && ` · ${itemsCount} ${t('studio.preview.items', { defaultValue: 'items' })}`}
              </p>
              {releaseNotes && (
                <p className="text-xs italic mt-2 line-clamp-3">&ldquo;{releaseNotes}&rdquo;</p>
              )}
            </div>

            {readiness?.publish_confirmation_required && (
              <div className="rounded-md bg-amber-100 border border-amber-300 dark:bg-amber-500/15 dark:border-amber-500/40 px-3 py-2 text-xs flex items-start gap-2" data-testid="studio-publish-warning-banner">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
                <div>
                  <p className="font-medium">
                    {t('studio.publish.manyWarnings', {
                      defaultValue: '{{n}} warnings detected.',
                      n: readiness?.severity_counts?.warning || 0,
                    })}
                  </p>
                  <label className="flex items-center gap-1.5 mt-1 cursor-pointer">
                    <Checkbox
                      checked={confirmAcked}
                      onCheckedChange={setConfirmAcked}
                      data-testid="studio-publish-ack-warnings"
                    />
                    <span>{t('studio.publish.ackWarnings', {
                      defaultValue: 'I have reviewed these warnings and want to publish anyway.',
                    })}</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="studio-publish-cancel" disabled={publishing}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={() => handlePublish(confirmAcked || !readiness?.publish_confirmation_required)}
            disabled={publishing || loading || blocksPublish || !target || needsConfirmation}
            data-testid="studio-publish-confirm"
          >
            {publishing && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            {t('studio.publish.confirm', { defaultValue: 'Publish now' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChecklistRow({ item, isEs }) {
  const Icon = item.passed ? CheckCircle2 : XCircle;
  const tone = item.passed ? 'text-success' : 'text-destructive';
  const label = isEs ? item.label_es : item.label_en;
  const reason = isEs ? item.reason_es : item.reason_en;
  return (
    <li
      data-testid={`studio-publish-checklist-${item.code}`}
      data-passed={item.passed ? 'true' : 'false'}
      className="flex items-start gap-2 rounded-md border border-border bg-card px-2.5 py-1.5"
    >
      <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium leading-snug">{label}</p>
        <p className="text-xs text-muted-foreground leading-snug">{reason}</p>
      </div>
    </li>
  );
}

/* ============================================================================
 *                          PUBLISH SUCCESS DIALOG
 * ========================================================================== */
export function PublishSuccessDialog({ open, onClose, summary, onViewCandidate }) {
  const { t } = useTranslation();
  if (!summary) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="studio-publish-success-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-600" />
            {t('studio.publish.successTitle', { defaultValue: 'Assessment published' })}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('studio.publish.successDesc', { defaultValue: 'Here is a summary of what was published.' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2" data-testid="studio-publish-summary">
          <SummaryRow label={t('studio.publish.summary.version', { defaultValue: 'Published version' })}
            value={`v${summary.published_version}`} testId="studio-publish-summary-version" />
          <SummaryRow label={t('studio.publish.summary.target', { defaultValue: 'Publish target' })}
            value={summary.publish_target_label} testId="studio-publish-summary-target" />
          <SummaryRow label={t('studio.publish.summary.sections', { defaultValue: 'Sections' })}
            value={summary.sections_count} testId="studio-publish-summary-sections" />
          <SummaryRow label={t('studio.publish.summary.items', { defaultValue: 'Content items' })}
            value={summary.items_count} testId="studio-publish-summary-items" />
          <SummaryRow label={t('studio.publish.summary.by', { defaultValue: 'Published by' })}
            value={summary.published_by_name || summary.published_by_id?.slice(0, 8) || '—'}
            testId="studio-publish-summary-by" />
          <SummaryRow label={t('studio.publish.summary.at', { defaultValue: 'Published at' })}
            value={new Date(summary.published_at).toLocaleString(getUiLocale())}
            testId="studio-publish-summary-at" />
          {summary.release_notes && (
            <SummaryRow label={t('studio.publish.summary.notes', { defaultValue: 'Release notes' })}
              value={summary.release_notes} testId="studio-publish-summary-notes" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="studio-publish-success-close">
            {t('common.close', { defaultValue: 'Close' })}
          </Button>
          <Button onClick={onViewCandidate} data-testid="studio-publish-view-candidate" className="gap-1">
            <Eye className="h-3 w-3" />
            {t('studio.publish.viewCandidate', { defaultValue: 'View as candidate' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 text-xs" data-testid={testId}>
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="font-medium text-right">{value || '—'}</span>
    </div>
  );
}

/* ============================================================================
 *                          PUBLISHED BADGE (in editor header)
 * ========================================================================== */
export function PublishedBadge({ questionnaire }) {
  const { t } = useTranslation();
  if (!questionnaire || questionnaire.status !== 'published') return null;
  const target = PUBLISH_TARGETS.find((t2) => t2.code === questionnaire.publish_target)?.label
                 || questionnaire.publish_target || '—';
  return (
    <Badge
      variant="outline"
      data-testid="studio-published-badge"
      className="bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40 text-xs gap-1 h-6 px-2"
    >
      <Sparkles className="h-3 w-3" />
      <span data-testid="studio-published-badge-version">
        v{questionnaire.meta_version || 1}
      </span>
      <span className="opacity-60">·</span>
      <span data-testid="studio-published-badge-target">{target}</span>
    </Badge>
  );
}

/* ============================================================================
 *                          FIND & REPLACE DIALOG
 * ========================================================================== */
export function FindReplaceDialog({ open, onClose, questions, patchQuestions }) {
  const { t } = useTranslation();
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);

  // Reset on each open.
  useEffect(() => { if (!open) { setFind(''); setReplace(''); } }, [open]);

  // Compute affected items LIVE on every keystroke.
  const affected = useMemo(() => {
    if (!find) return [];
    const flags = caseSensitive ? 'g' : 'gi';
    const escaped = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = wholeWord ? `\\b${escaped}\\b` : escaped;
    let rx;
    try { rx = new RegExp(pattern, flags); } catch { return []; }
    const out = [];
    for (const q of (questions || [])) {
      const prompt = q.prompt || '';
      const matches = (prompt.match(rx) || []).length;
      if (matches > 0) {
        out.push({
          ...q,
          matches,
          previewBefore: prompt,
          previewAfter: prompt.replace(rx, replace),
        });
      }
    }
    return out;
  }, [find, replace, caseSensitive, wholeWord, questions]);

  const handleApply = async () => {
    if (affected.length === 0) {
      toast.message(t('studio.findReplace.noMatches', { defaultValue: 'No matches.' }));
      return;
    }
    const affectedIds = new Set(affected.map((a) => a.id));
    const flags = caseSensitive ? 'g' : 'gi';
    const escaped = find.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = wholeWord ? `\\b${escaped}\\b` : escaped;
    const rx = new RegExp(pattern, flags);
    const next = questions.map((q) => {
      if (!affectedIds.has(q.id)) return q;
      return { ...q, prompt: (q.prompt || '').replace(rx, replace) };
    });
    await Promise.resolve(patchQuestions(next));
    toast.success(t('studio.findReplace.applied', {
      defaultValue: 'Replaced in {{n}} item(s).',
      n: affected.length,
      count: affected.length,
    }));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="studio-find-replace-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Search className="h-4 w-4" />
            {t('studio.findReplace.title', { defaultValue: 'Find & Replace' })}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('studio.findReplace.subtitle', {
              defaultValue: 'Replace text across content prompts. Preview every change before applying.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium block mb-1">
                {t('studio.findReplace.find', { defaultValue: 'Find' })}
              </label>
              <Input
                value={find}
                onChange={(e) => setFind(e.target.value)}
                data-testid="studio-find-replace-find"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">
                {t('studio.findReplace.replace', { defaultValue: 'Replace' })}
              </label>
              <Input
                value={replace}
                onChange={(e) => setReplace(e.target.value)}
                data-testid="studio-find-replace-replace"
              />
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <label className="flex items-center gap-1 cursor-pointer">
              <Checkbox
                checked={caseSensitive}
                onCheckedChange={setCaseSensitive}
                data-testid="studio-find-replace-case"
              />
              {t('studio.findReplace.case', { defaultValue: 'Case sensitive' })}
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <Checkbox
                checked={wholeWord}
                onCheckedChange={setWholeWord}
                data-testid="studio-find-replace-word"
              />
              {t('studio.findReplace.wholeWord', { defaultValue: 'Whole word' })}
            </label>
            <span className="ml-auto text-muted-foreground" data-testid="studio-find-replace-match-count">
              {t('studio.findReplace.matches', { defaultValue: '{{n}} item(s) affected', n: affected.length, count: affected.length })}
            </span>
          </div>
          <div className="max-h-72 overflow-y-auto border border-border rounded-md bg-muted/30 p-2 space-y-1.5" data-testid="studio-find-replace-preview">
            {affected.length === 0 && (
              <p className="text-xs text-muted-foreground italic p-2 text-center">
                {find
                  ? t('studio.findReplace.noMatches', { defaultValue: 'No matches yet.' })
                  : t('studio.findReplace.previewHint', { defaultValue: 'Type a search term to preview matches.' })}
              </p>
            )}
            {affected.map((a) => (
              <div
                key={a.id}
                data-testid={`studio-find-replace-row-${a.id}`}
                className="rounded border border-border bg-card px-2 py-1.5"
              >
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono">
                  {a.stable_id || a.id.slice(0, 8)} · {a.matches} match{a.matches > 1 ? 'es' : ''}
                </p>
                <p className="text-xs text-destructive line-through line-clamp-1">{a.previewBefore}</p>
                <p className="text-xs text-success line-clamp-1">{a.previewAfter}</p>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="studio-find-replace-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            onClick={handleApply}
            disabled={affected.length === 0}
            data-testid="studio-find-replace-apply"
          >
            {t('studio.findReplace.apply', { defaultValue: 'Replace all' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// End of file.
